import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PageWrapper from '../PageWrapper/PageWrapper';
import { AdminRestricted } from '../../components/AdminRestricted/AdminRestricted';
import { CardV2, CardHeader, CardTitle, CardContent } from '../../components/CardV2/CardV2';
import { ButtonV2 } from '../../components/ButtonV2/ButtonV2';
import { TabV2, TabsContent, TabsList, TabsTrigger } from '../../components/TabV2/TabV2';
import { 
  BarChart3, 
  TrendingUp, 
  ShoppingBag, 
  Store, 
  Filter,
  Package
} from 'lucide-react';
import { useGetShops } from '../../hooks/useGetShops';
import FilterPill from '../../components/FilterPill/FilterPill';
import { ShopClient } from '../../api/ShopClientsAPI';
import { Link } from '../../components/Link/Link';
import Card from '../../components/Card/Card';
import { useStatistics } from '../../hooks/useStatistics';
import BarChart from '../../components/BarChart/BarChart';
import NoData from '../../components/NoData/NoData';
import { Settings } from '../../store/reducers/AccountSlice';
import CONFIG from '../../config/config';
import { orderStatusToTranslationKeys, orderStatusToColor } from '../../shared/orderStatusToTranslationKey';
import { Icon } from '../../components/Icon/Icon';

type TimeRange = { start: string; end: string };

const toISODate = (d: Date) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString();
};

// Production stages for orders - will be dynamically generated from config
const getProductionStages = (settings: Settings | null, t: (key: string) => string) => {
  const allOrderStatuses = CONFIG.ORDERS.STATUS;
  
  // Use configured statuses if available, otherwise fall back to all statuses
  const baseStatuses = (settings?.isConfigurationLoaded && 
                       settings?.allowedOrderStatuses && 
                       settings.allowedOrderStatuses.length > 0)
    ? settings.allowedOrderStatuses
    : allOrderStatuses;
  
  
  // Remove duplicates and create production stages
  const uniqueStatuses = Array.from(new Set(baseStatuses));
  
  return uniqueStatuses
    .map(status => ({
      key: status,
      label: t(orderStatusToTranslationKeys(status)),
      color: orderStatusToColor(status)
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

type ProductionStage = {
  key: string;
  label: string;
  color: string;
  count: number;
};

// Overview Tab Components
const TotalOrdersCard = ({ timeRange, selectedPartnerId }: { timeRange: TimeRange; selectedPartnerId: string }) => {
  const { t } = useTranslation();
  const filters = useMemo(() => ({
    startTime: timeRange.start,
    endTime: timeRange.end,
    ...(selectedPartnerId && { shopId: selectedPartnerId })
  }), [timeRange.start, timeRange.end, selectedPartnerId]);
  const data = useStatistics('orders-total-per-year', filters);
  const partnersData = useStatistics('total-sales-by-shop-grouped-by-year', filters);
  
  const totalOrders = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return 0;
    
    // Handle nested structure: { [year]: { [modelName]: number } }
    let total = 0;
    Object.keys(data).forEach(year => {
      const yearData = data[year];
      if (typeof yearData === 'object' && yearData !== null) {
        Object.values(yearData).forEach((count: any) => {
          total += typeof count === 'number' ? count : 0;
        });
      } else if (typeof yearData === 'number') {
        total += yearData;
      }
    });
    
    return total;
  }, [data]);

  const tableData = useMemo(() => {
    if (!partnersData || Object.keys(partnersData).length === 0) return [];
    
    // Handle nested structure: { [year]: { [shopName]: number } }
    const allShops = new Set<string>();
    const years = Object.keys(partnersData);
    
    // Collect all unique shop names
    years.forEach(year => {
      if (typeof partnersData[year] === 'object' && partnersData[year] !== null) {
        Object.keys(partnersData[year]).forEach(shop => allShops.add(shop));
      }
    });
    
    const shopNames = Array.from(allShops);
    const tableData = shopNames.map(shopName => {
      let total = 0;
      years.forEach(year => {
        if (typeof partnersData[year] === 'object' && partnersData[year] !== null) {
          total += (partnersData[year][shopName] || 0);
        }
      });
      return {
        name: shopName,
        count: total
      };
    }).sort((a, b) => b.count - a.count);
    
    return tableData;
  }, [partnersData]);

  return (
    <div className="space-y-4">
      {/* Total Orders Card */}
      <CardV2>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            {t('stats.total_orders') || 'Total Orders'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-foreground">{totalOrders.toLocaleString()}</div>
          <p className="text-sm text-muted-foreground mt-1">
            {t('stats.all_time') || 'All time'}
          </p>
        </CardContent>
      </CardV2>

      {/* Partners Table */}
      {tableData.length > 0 && (
        <CardV2>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              {t('stats.orders_per_partners_table') || 'Orders per Partners - Detailed View'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium text-muted-foreground">
                      {t('stats.partner_name') || 'Partner Name'}
                    </th>
                    <th className="text-right p-3 font-medium text-muted-foreground">
                      {t('stats.total_orders') || 'Total Orders'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((item, index) => (
                    <tr key={item.name} className="border-b hover:bg-muted/50">
                      <td className="p-3">
                        <span className="font-medium text-foreground">
                          {item.name}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-lg font-bold text-foreground">
                          {item.count.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </CardV2>
      )}
    </div>
  );
};


const OrdersPerPartnersChart = ({ timeRange, selectedPartnerId }: { timeRange: TimeRange; selectedPartnerId: string }) => {
  const { t } = useTranslation();
  const filters = useMemo(() => ({
    startTime: timeRange.start,
    endTime: timeRange.end,
    ...(selectedPartnerId && { shopId: selectedPartnerId })
  }), [timeRange.start, timeRange.end, selectedPartnerId]);
  const data = useStatistics('total-sales-by-shop-grouped-by-year', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    console.log('OrdersPerPartnersChart data:', data);
    
    // Handle nested structure: { [year]: { [shopName]: number } }
    const allShops = new Set<string>();
    const years = Object.keys(data);
    
    // Collect all unique shop names
    years.forEach(year => {
      if (typeof data[year] === 'object' && data[year] !== null) {
        Object.keys(data[year]).forEach(shop => allShops.add(shop));
      }
    });
    
    const shopNames = Array.from(allShops);
    const values = shopNames.map(shopName => {
      let total = 0;
      years.forEach(year => {
        if (typeof data[year] === 'object' && data[year] !== null) {
          total += (data[year][shopName] || 0);
        }
      });
      return total;
    });
    
    console.log('OrdersPerPartnersChart processed data:', { shopNames, values });
    
    return [{
      label: t('stats.orders') || 'Orders',
      data: values,
      backgroundColor: ['rgba(59, 130, 246, 0.6)'],
      borderColor: ['rgba(59, 130, 246, 0.8)'],
      borderWidth: 1,
    }];
  }, [data, t]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Store className="h-5 w-5" />
          {t('stats.orders_per_partners') || 'Orders per Partners'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length > 0 ? (
          <BarChart 
            title={t('stats.orders_per_partners') || 'Orders per Partners'} 
            xLabels={chartData[0] ? Object.keys(data || {}).reduce((shops: string[], year) => {
              if (typeof data[year] === 'object' && data[year] !== null) {
                Object.keys(data[year]).forEach(shop => {
                  if (!shops.includes(shop)) shops.push(shop);
                });
              }
              return shops;
            }, []) : []} 
            dataSets={chartData} 
          />
        ) : (
          <NoData message={t("global.empty_results")} />
        )}
      </CardContent>
    </CardV2>
  );
};

// Orders Tab Components
const OrdersInProduction = ({ timeRange, selectedPartnerId }: { timeRange: TimeRange; selectedPartnerId: string }) => {
  const { t } = useTranslation();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const filters = useMemo(() => ({
    startTime: timeRange.start,
    endTime: timeRange.end,
    ...(selectedPartnerId && { shopId: selectedPartnerId })
  }), [timeRange.start, timeRange.end, selectedPartnerId]);
  const data = useStatistics('active-orders-grouped-by-status', filters);
  
  const productionStages = useMemo(() => getProductionStages(settings, t), [settings, t]);
  
  const productionData = useMemo((): ProductionStage[] => {
    if (!data || Object.keys(data).length === 0) {
      return productionStages.map(stage => ({ ...stage, count: 0 }));
    }
    
    return productionStages.map(stage => {
      const countValue = data[stage.key];
      const count = typeof countValue === 'number' ? countValue : 0;
      return {
        ...stage,
        count
      };
    });
  }, [data, productionStages]);
  
  // Show loading if configuration is not loaded yet
  if (!settings?.isConfigurationLoaded) {
    return (
      <CardV2>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon url="📊" />
            {t('stats.orders_in_production') || 'Orders in Production'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading configuration...</div>
          </div>
        </CardContent>
      </CardV2>
    );
  }

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          {t('stats.orders_in_production') || 'Orders in Production'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
          {productionData.length > 0 ? (
            productionData.map((stage) => (
              <div key={stage.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                  <span className="font-medium">{stage.label}</span>
                </div>
                <span className="text-lg font-bold text-foreground">{stage.count}</span>
              </div>
            ))
          ) : (
            <NoData message={t("global.empty_results")} />
          )}
        </div>
      </CardContent>
    </CardV2>
  );
};

const MostSoldItems = ({ timeRange, selectedPartnerId }: { timeRange: TimeRange; selectedPartnerId: string }) => {
  const { t } = useTranslation();
  const filters = useMemo(() => ({
    startTime: timeRange.start,
    endTime: timeRange.end,
    ...(selectedPartnerId && { shopId: selectedPartnerId })
  }), [timeRange.start, timeRange.end, selectedPartnerId]);
  const data = useStatistics('sold-dresses-per-year', filters);
  
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    console.log('MostSoldItems data:', data);
    
    // Handle nested structure: { [year]: { [dressName]: number } }
    const allDresses = new Set<string>();
    const years = Object.keys(data);
    
    // Collect all unique dress names
    years.forEach(year => {
      if (typeof data[year] === 'object' && data[year] !== null) {
        Object.keys(data[year]).forEach(dress => allDresses.add(dress));
      }
    });
    
    const dressNames = Array.from(allDresses);
    const values = dressNames.map(dressName => {
      let total = 0;
      years.forEach(year => {
        if (typeof data[year] === 'object' && data[year] !== null) {
          total += (data[year][dressName] || 0);
        }
      });
      return total;
    });
    
    console.log('MostSoldItems processed data:', { dressNames, values });
    
    return [{
      label: t('stats.sold_items') || 'Sold Items',
      data: values,
      backgroundColor: ['rgba(16, 185, 129, 0.6)'],
      borderColor: ['rgba(16, 185, 129, 0.8)'],
      borderWidth: 1,
    }];
  }, [data, t]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          {t('stats.most_sold_items') || 'Most Sold Items'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-96 overflow-y-auto">
          {chartData.length > 0 ? (
            <BarChart 
              title={t('stats.most_sold_items') || 'Most Sold Items'} 
              xLabels={chartData[0] ? Object.keys(data || {}).reduce((dresses: string[], year) => {
                if (typeof data[year] === 'object' && data[year] !== null) {
                  Object.keys(data[year]).forEach(dress => {
                    if (!dresses.includes(dress)) dresses.push(dress);
                  });
                }
                return dresses;
              }, []) : []} 
              dataSets={chartData} 
            />
          ) : (
            <NoData message={t("global.empty_results")} />
          )}
        </div>
      </CardContent>
    </CardV2>
  );
};

const MostSoldItemsTable = ({ timeRange, selectedPartnerId }: { timeRange: TimeRange; selectedPartnerId: string }) => {
  const { t } = useTranslation();
  const filters = useMemo(() => ({
    startTime: timeRange.start,
    endTime: timeRange.end,
    ...(selectedPartnerId && { shopId: selectedPartnerId })
  }), [timeRange.start, timeRange.end, selectedPartnerId]);
  const data = useStatistics('sold-dresses-per-year', filters);
  
  const tableData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];
    
    // Handle nested structure: { [year]: { [dressName]: number } }
    const allDresses = new Set<string>();
    const years = Object.keys(data);
    
    // Collect all unique dress names
    years.forEach(year => {
      if (typeof data[year] === 'object' && data[year] !== null) {
        Object.keys(data[year]).forEach(dress => allDresses.add(dress));
      }
    });
    
    const dressNames = Array.from(allDresses);
    const tableData = dressNames.map(dressName => {
      let total = 0;
      years.forEach(year => {
        if (typeof data[year] === 'object' && data[year] !== null) {
          total += (data[year][dressName] || 0);
        }
      });
      return {
        name: dressName,
        count: total
      };
    }).sort((a, b) => b.count - a.count).map((item, index) => ({
      ...item,
      rank: index + 1
    }));
    
    return tableData;
  }, [data]);

  return (
    <CardV2>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          {t('stats.most_sold_items_table') || 'Most Sold Items - Table'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-96 overflow-y-auto overflow-x-auto">
          {tableData.length > 0 ? (
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="border-b">
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    {t('stats.rank') || 'Rank'}
                  </th>
                  <th className="text-left p-3 font-medium text-muted-foreground">
                    {t('stats.item_name') || 'Item Name'}
                  </th>
                  <th className="text-right p-3 font-medium text-muted-foreground">
                    {t('stats.total_sold') || 'Total Sold'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((item, index) => (
                  <tr key={item.name} className="border-b hover:bg-muted/50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          #{item.rank}
                        </span>
                        {item.rank <= 3 && (
                          <span className={`text-lg ${
                            item.rank === 1 ? 'text-yellow-500' : 
                            item.rank === 2 ? 'text-gray-400' : 
                            'text-amber-600'
                          }`}>
                            {item.rank === 1 ? '🥇' : item.rank === 2 ? '🥈' : '🥉'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="font-medium text-foreground">
                        {item.name}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-lg font-bold text-foreground">
                        {item.count.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <NoData message={t("global.empty_results")} />
          )}
        </div>
      </CardContent>
    </CardV2>
  );
};

function OrdersStatisticsPageV3() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Time range state
  const monthStart = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthEnd = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [timeRange, setTimeRange] = useState<TimeRange>({ start: toISODate(monthStart), end: toISODate(monthEnd) });
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [partnerSearchText, setPartnerSearchText] = useState('');
  
  const { shops } = useGetShops();

  const onDateChange = (key: 'start' | 'end', value: string) => {
    const date = new Date(value);
    setTimeRange((prev) => ({ ...prev, [key]: toISODate(date) }));
  };

  const startInputValue = useMemo(() => new Date(timeRange.start).toISOString().slice(0, 10), [timeRange.start]);
  const endInputValue = useMemo(() => new Date(timeRange.end).toISOString().slice(0, 10), [timeRange.end]);

  // Filter shops by search text
  const filteredShops = shops.filter(shop => 
    shop.name.toLowerCase().includes(partnerSearchText.toLowerCase())
  );

  const setPartnerFilter = (shop: ShopClient) => {
    setSelectedPartnerId(prev => prev === String(shop.id) ? '' : String(shop.id));
  };

  return (
    <PageWrapper>
      <AdminRestricted>
        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              <h1 className="text-3xl font-bold text-foreground">
                {t('stats.orders_statistics') || 'Orders Statistics'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.from') || 'From'}</span>
                <input 
                  type="date" 
                  value={startInputValue} 
                  onChange={(e) => onDateChange('start', e.target.value)} 
                  className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" 
                />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('stats.filters.to') || 'To'}</span>
                <input 
                  type="date" 
                  value={endInputValue} 
                  onChange={(e) => onDateChange('end', e.target.value)} 
                  className="px-3 py-1 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground" 
                />
              </div>
            </div>
          </div>

          {/* Shared Filters Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
            <ButtonV2
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? t('stats.filters.hide') || 'Hide Filters' : t('stats.filters.show') || 'Show Filters'}
            </ButtonV2>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <Card>
              <div className="space-y-6">
                {/* Partner Filter Section */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">{t('stats.filters.partners') || 'Partners'}</h3>
                  <div className="w-full max-w-full">
                    <label>
                      <span className="autocompleteInputWrapper formValue autocomplete">
                        <input
                          className="p-3 w-full border-stone-200 border rounded focus:outline-none"
                          type="text"
                          placeholder={t('stats.filters.search_partners') || 'Search partners...'}
                          value={partnerSearchText}
                          onChange={(e) => setPartnerSearchText(e.target.value)}
                          data-dd-action-name={'Partner Search'}
                        />
                      </span>
                    </label>
                  </div>
                  <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                    {filteredShops.length > 0 ? filteredShops.map((s, i) => (
                      <FilterPill 
                        isActive={selectedPartnerId === String(s.id)} 
                        key={i} 
                        text={s.name} 
                        fn={() => setPartnerFilter(s)} 
                      />
                    ))
                    : partnerSearchText ? (
                      <p>{t('stats.filters.no_partners_found') || 'No partners found'}</p>
                    ) : (
                      <>
                        <p>{t('stats.filters.no_partners_available') || 'No partners available'}</p>&nbsp;
                        <Link url="/partners" text={t('stats.filters.add_partners') || 'Add Partners'}/>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Main Content Tabs */}
          <TabV2 value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                {t('stats.overview') || 'Overview'}
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex items-center gap-2">
                <ShoppingBag className="h-4 w-4" />
                {t('stats.orders') || 'Orders'}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 max-h-screen overflow-y-auto">
              {/* Total Orders Card */}
              <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
                <div className="min-h-0 lg:col-span-2">
                  <TotalOrdersCard timeRange={timeRange} selectedPartnerId={selectedPartnerId} />
                </div>
                <div className="min-h-0 lg:col-span-4">
                  <OrdersPerPartnersChart timeRange={timeRange} selectedPartnerId={selectedPartnerId} />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="orders" className="space-y-6 max-h-screen overflow-y-auto">
              {/* Orders Content */}
              {/* Tables Row - Side by Side */}
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <OrdersInProduction timeRange={timeRange} selectedPartnerId={selectedPartnerId} />
                </div>
                <div className="flex-1">
                  <MostSoldItemsTable timeRange={timeRange} selectedPartnerId={selectedPartnerId} />
                </div>
              </div>
              
              {/* Chart Row - Full Width Below Tables */}
              <div className="w-full">
                <MostSoldItems timeRange={timeRange} selectedPartnerId={selectedPartnerId} />
              </div>
            </TabsContent>
          </TabV2>
        </div>
      </AdminRestricted>
    </PageWrapper>
  );
}

export default OrdersStatisticsPageV3;
